import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, Send, Image, Loader2, RotateCcw, RotateCw, FlipHorizontal, FlipVertical, ZoomIn, Pencil, Check, ImagePlus, Trash2, BadgeCheck, CheckCheck, Eye } from 'lucide-react';
import styles from './DoubtDetailDrawer.module.css';
import { db, auth } from '../../firebase';
import {
  collection, addDoc, onSnapshot, orderBy, query,
  doc, updateDoc, increment, serverTimestamp, deleteField, deleteDoc,
} from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { STREAMS } from '../../data/doubtStreams';
import MathText from './MathText';
import { toast } from 'react-toastify';

function timeAgo(ts) {
  if (!ts) return '';
  const date = ts?.toDate ? ts.toDate() : new Date(ts);
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function formatDateTime(ts) {
  if (!ts) return '';
  const date = ts?.toDate ? ts.toDate() : new Date(ts);
  return date.toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

const R2_ENABLED = process.env.REACT_APP_R2_ENABLED === 'true';
// Replies before this date predate seen-tracking; treat missing seenAt as unknown, not 'unseen'
const SEEN_TRACKING_START = new Date('2026-08-15T00:00:00+05:30');

async function compressAndUploadReply(file, doubtId, uid) {
  // Validate type
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
    throw new Error('Only JPEG, PNG, or WebP images are allowed.');
  }
  if (file.size > 8 * 1024 * 1024) throw new Error('Image must be under 8 MB.');

  // Compress in canvas
  const compressed = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = evt => {
      const img = new window.Image();
      img.onerror = reject;
      img.onload = () => {
        const MAX = 1280;
        let w = img.width, h = img.height;
        if (w > MAX || h > MAX) {
          if (w > h) { h = Math.round(h * MAX / w); w = MAX; }
          else        { w = Math.round(w * MAX / h); h = MAX; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        canvas.toBlob(b => b ? resolve(b) : reject(new Error('Compression failed')), 'image/jpeg', 0.82);
      };
      img.src = evt.target.result;
    };
    reader.readAsDataURL(file);
  });

  // Get presigned URL
  const idToken = await auth.currentUser.getIdToken();
  const folder = `doubts/${doubtId}/replies/${uid}`;
  // Convert blob to base64 and upload via server proxy (avoids R2 CORS on presigned URLs)
  const base64 = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = e => resolve(e.target.result.split(',')[1]);
    reader.readAsDataURL(compressed);
  });
  const res = await fetch('/api/upload-image', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
    body: JSON.stringify({ imageBase64: base64, contentType: 'image/jpeg', folder }),
  });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Could not upload image'); }
  const { publicUrl } = await res.json();
  return publicUrl;
}

// Generic compress + upload used for admin image replacements
async function compressAndUploadAdmin(file, folder) {
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) throw new Error('Only JPEG, PNG, or WebP images are allowed.');
  if (file.size > 8 * 1024 * 1024) throw new Error('Image must be under 8 MB.');
  const compressed = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = evt => {
      const img = new window.Image();
      img.onerror = reject;
      img.onload = () => {
        const MAX = 1280;
        let w = img.width, h = img.height;
        if (w > MAX || h > MAX) { if (w > h) { h = Math.round(h * MAX / w); w = MAX; } else { w = Math.round(w * MAX / h); h = MAX; } }
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        canvas.toBlob(b => b ? resolve(b) : reject(new Error('Compression failed')), 'image/jpeg', 0.82);
      };
      img.src = evt.target.result;
    };
    reader.readAsDataURL(file);
  });
  const idToken = await auth.currentUser.getIdToken();
  const base64 = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = e => resolve(e.target.result.split(',')[1]);
    reader.readAsDataURL(compressed);
  });
  const res = await fetch('/api/upload-image', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
    body: JSON.stringify({ imageBase64: base64, contentType: 'image/jpeg', folder }),
  });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Could not upload image'); }
  const { publicUrl } = await res.json();
  return publicUrl;
}

function ImgCtrl({ t, onRotateL, onRotateR, onFlipH, onFlipV, onZoom, onChangeImg, onDeleteImg }) {
  return (
    <div className={styles.imgCtrl}>
      <button className={styles.imgCtrlBtn} onClick={e => { e.stopPropagation(); onRotateL(); }} title="Rotate left"><RotateCcw size={13} /></button>
      <button className={styles.imgCtrlBtn} onClick={e => { e.stopPropagation(); onRotateR(); }} title="Rotate right"><RotateCw size={13} /></button>
      <button className={`${styles.imgCtrlBtn} ${t.flipH ? styles.imgCtrlBtnOn : ''}`} onClick={e => { e.stopPropagation(); onFlipH(); }} title="Flip horizontal"><FlipHorizontal size={13} /></button>
      <button className={`${styles.imgCtrlBtn} ${t.flipV ? styles.imgCtrlBtnOn : ''}`} onClick={e => { e.stopPropagation(); onFlipV(); }} title="Flip vertical"><FlipVertical size={13} /></button>
      <button className={`${styles.imgCtrlBtn} ${styles.imgCtrlBtnZoom}`} onClick={e => { e.stopPropagation(); onZoom(); }} title="View full size"><ZoomIn size={13} /></button>
      {(onChangeImg || onDeleteImg) && <span className={styles.imgCtrlDivider} />}
      {onChangeImg && <button className={styles.imgCtrlBtn} onClick={e => { e.stopPropagation(); onChangeImg(); }} title="Replace image"><ImagePlus size={13} /></button>}
      {onDeleteImg && <button className={`${styles.imgCtrlBtn} ${styles.imgCtrlBtnDanger}`} onClick={e => { e.stopPropagation(); onDeleteImg(); }} title="Delete image"><Trash2 size={13} /></button>}
    </div>
  );
}

export default function DoubtDetailDrawer({ doubt, onClose }) {
  const { currentUser, userData } = useAuth();
  const isAdmin = userData?.isAdmin === true || userData?.isSuperAdmin === true;
  const isStudent = !!currentUser && !isAdmin;

  // Live copy of the doubt document so status / metadata always reflects Firestore
  const [liveDoubt, setLiveDoubt] = useState(doubt);
  const canReply = isAdmin || (isStudent && liveDoubt?.userId === currentUser?.uid);

  const [replies, setReplies] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [replyImageFile, setReplyImageFile] = useState(null);
  const [replyImagePreview, setReplyImagePreview] = useState(null);
  const [uploadingImg, setUploadingImg] = useState(false);
  const imageInputRef = useRef(null);
  const bottomRef = useRef(null);

  // Per-image rotate / flip state — initialised from Firestore, auto-saved on every change
  const [qTransform, setQTransform] = useState(
    doubt?.imageTransform || { rotation: 0, flipH: false, flipV: false }
  );
  const [replyTransforms, setReplyTransforms] = useState({});
  const qTransformMounted = useRef(false);
  useEffect(() => {
    // skip initial mount so we don't overwrite Firestore with the default value
    if (!qTransformMounted.current) { qTransformMounted.current = true; return; }
    updateDoc(doc(db, 'doubts', liveDoubt.id), { imageTransform: qTransform }).catch(console.error);
  }, [qTransform]);

  // Inline editing (admin only)
  const [editingQuestion, setEditingQuestion] = useState(false);
  const [questionEditText, setQuestionEditText] = useState('');
  const [editingReplyId, setEditingReplyId] = useState(null);
  const [replyEditText, setReplyEditText] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  async function saveQuestion() {
    if (savingEdit) return;
    setSavingEdit(true);
    try {
      await updateDoc(doc(db, 'doubts', liveDoubt.id), { questionText: questionEditText.trim() });
      setEditingQuestion(false);
      toast.success('Question updated.');
    } catch (err) {
      console.error(err);
      toast.error('Failed to save. Please try again.');
    } finally { setSavingEdit(false); }
  }

  async function saveReply() {
    if (savingEdit) return;
    setSavingEdit(true);
    try {
      await updateDoc(doc(db, 'doubts', liveDoubt.id, 'replies', editingReplyId), { text: replyEditText.trim() });
      setEditingReplyId(null);
      toast.success('Message updated.');
    } catch (err) {
      console.error(err);
      toast.error('Failed to save. Please try again.');
    } finally { setSavingEdit(false); }
  }

  // Admin image replace / delete
  const qImgInputRef = useRef(null);
  const replyImgInputRef = useRef(null);
  const imgEditTargetReplyId = useRef(null);
  const [imgEditSaving, setImgEditSaving] = useState(false);

  async function handleQImgFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setImgEditSaving(true);
    try {
      const url = await compressAndUploadAdmin(file, `doubts/${liveDoubt.id}/question/${currentUser.uid}`);
      await updateDoc(doc(db, 'doubts', liveDoubt.id), { imageURL: url });
      toast.success('Question image updated.');
    } catch (err) { console.error(err); toast.error(`Upload failed: ${err.message}`); }
    finally { setImgEditSaving(false); }
  }

  async function deleteQImg() {
    if (!window.confirm('Delete the question image? This cannot be undone.')) return;
    try {
      await updateDoc(doc(db, 'doubts', liveDoubt.id), { imageURL: deleteField() });
      toast.success('Question image deleted.');
    } catch (err) { console.error(err); toast.error('Failed to delete image.'); }
  }

  async function handleReplyImgFileChange(e) {
    const file = e.target.files?.[0];
    if (!file || !imgEditTargetReplyId.current) return;
    e.target.value = '';
    const replyId = imgEditTargetReplyId.current;
    imgEditTargetReplyId.current = null;
    setImgEditSaving(true);
    try {
      const url = await compressAndUploadAdmin(file, `doubts/${liveDoubt.id}/replies/${currentUser.uid}`);
      await updateDoc(doc(db, 'doubts', liveDoubt.id, 'replies', replyId), { imageURL: url });
      toast.success('Image updated.');
    } catch (err) { console.error(err); toast.error(`Upload failed: ${err.message}`); }
    finally { setImgEditSaving(false); }
  }

  async function deleteReplyImg(replyId) {
    if (!window.confirm('Delete this image? This cannot be undone.')) return;
    try {
      await updateDoc(doc(db, 'doubts', liveDoubt.id, 'replies', replyId), { imageURL: deleteField() });
      toast.success('Image deleted.');
    } catch (err) { console.error(err); toast.error('Failed to delete image.'); }
  }

  async function deleteReply(replyId) {
    if (!window.confirm('Delete this message? This cannot be undone.')) return;
    try {
      await deleteDoc(doc(db, 'doubts', liveDoubt.id, 'replies', replyId));
      toast.success('Message deleted.');
    } catch (err) { console.error(err); toast.error('Failed to delete message.'); }
  }
  const [lightbox, setLightbox] = useState(null);
  const [seenPopup, setSeenPopup] = useState(null); // reply.id whose popup is open // { src, rotation, flipH, flipV }
  function openLightbox(src, t) { setLightbox({ src, rotation: t.rotation, flipH: t.flipH, flipV: t.flipV }); }
  function closeLightbox() { setLightbox(null); }

  // Lock body scroll while open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Close on Escape
  const handleEscape = useCallback((e) => {
    if (e.key === 'Escape') { if (lightbox) closeLightbox(); else onClose(); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, lightbox]);
  useEffect(() => {
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleEscape]);

  // Real-time replies listener
  useEffect(() => {
    if (!doubt?.id) return;
    // Live listener for the doubt document itself (keeps status / counts fresh)
    const unsubDoubt = onSnapshot(doc(db, 'doubts', doubt.id), snap => {
      if (snap.exists()) setLiveDoubt({ id: snap.id, ...snap.data() });
    });
    return unsubDoubt;
  }, [doubt?.id]);

  useEffect(() => {
    if (!doubt?.id) return;
    const q = query(collection(db, 'doubts', doubt.id, 'replies'), orderBy('createdAt', 'asc'));
    const unsub = onSnapshot(q, snap => {
      setReplies(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [doubt?.id]);

  // Scroll to bottom when replies arrive
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [replies]);

  // Close seen-info popover on any outside click
  useEffect(() => {
    if (!seenPopup) return;
    const close = () => setSeenPopup(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [seenPopup]);

  // Mark all unseen admin replies as seen when a student opens the drawer
  useEffect(() => {
    if (!isStudent || !replies.length || !liveDoubt?.id) return;
    const unseen = replies.filter(r => r.roleLabel === 'PrrepX Team' && !r.seenAt);
    unseen.forEach(r =>
      updateDoc(doc(db, 'doubts', liveDoubt.id, 'replies', r.id), { seenAt: serverTimestamp() }).catch(console.error)
    );
  }, [replies, isStudent, liveDoubt?.id]);

  function handleImageSelect(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setReplyImageFile(file);
    const url = URL.createObjectURL(file);
    setReplyImagePreview(url);
  }

  function clearImage() {
    setReplyImageFile(null);
    setReplyImagePreview(null);
    if (imageInputRef.current) imageInputRef.current.value = '';
  }

  async function handleSend() {
    const hasText = replyText.trim().length > 0;
    const hasImage = !!replyImageFile;
    if (!hasText && !hasImage) return;
    if (sending) return;

    setSending(true);
    let imageURL = null;

    try {
      // Upload image if attached and R2 is enabled
      if (hasImage && R2_ENABLED) {
        setUploadingImg(true);
        try {
          imageURL = await compressAndUploadReply(replyImageFile, doubt.id, currentUser.uid);
        } catch (err) {
          toast.error(`Image upload failed: ${err.message}`);
          setSending(false);
          setUploadingImg(false);
          return;
        }
        setUploadingImg(false);
      }

      await addDoc(collection(db, 'doubts', doubt.id, 'replies'), {
        userId: currentUser.uid,
        doubtUserId: doubt.userId,
        userName: userData?.username || currentUser.displayName || (isAdmin ? 'PrrepX Team' : 'Student'),
        userPhotoURL: currentUser.photoURL || '',
        roleLabel: isAdmin ? 'PrrepX Team' : null,
        text: replyText.trim(),
        imageURL,
        createdAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'doubts', doubt.id), {
        replyCount: increment(1),
        lastActivityAt: serverTimestamp(),
        status: isAdmin ? 'answered' : 'open',
        lastRepliedByRole: isAdmin ? 'admin' : 'student',
        lastReplyText: replyText.trim() || '(image attached)',
        lastReplyBy: isAdmin ? 'PrrepX Team' : (userData?.username || currentUser.displayName || 'Student'),
        ...(isAdmin ? {} : { studentHasReplied: true }),
      });
      setReplyText('');
      clearImage();
      toast.success(isAdmin ? 'Answer sent!' : 'Follow-up sent!');
    } catch (err) {
      console.error(err);
      toast.error('Failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  }

  if (!liveDoubt) return null;

  const streamLabel = STREAMS[liveDoubt.stream]?.label ?? liveDoubt.stream;
  const initial = (liveDoubt.userName?.[0] ?? '?').toUpperCase();

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.panel} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">

        {/* Header */}
        <div className={styles.header}>
          <button className={styles.backBtn} onClick={onClose} aria-label="Close">
            <ChevronLeft size={18} />
            <span>Back</span>
          </button>
          <div className={styles.headerTags}>
            <span className={styles.streamBadge}>{streamLabel}</span>
            {liveDoubt.subject && <span className={styles.subjectBadge}>{liveDoubt.subject}</span>}
          </div>
          <button className={styles.closeX} onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className={styles.body}>
          {/* Original question card */}
          <div className={styles.questionCard}>
            <div className={styles.userRow}>
              {liveDoubt.userPhotoURL
                ? <img src={liveDoubt.userPhotoURL} alt="" className={styles.avatar} referrerPolicy="no-referrer" loading="lazy" />
                : <div className={styles.avatarFallback}>{initial}</div>
              }
              <div className={styles.userMeta}>
                <span className={styles.userName}>{liveDoubt.userName || 'Student'}</span>
                <span className={styles.time}>{formatDateTime(liveDoubt.submittedAt || liveDoubt.createdAt)} · {timeAgo(liveDoubt.submittedAt || liveDoubt.createdAt)}</span>
              </div>
              <span className={`${styles.statusPill} ${liveDoubt.status === 'answered' ? styles.statusAnswered : styles.statusOpen}`}>
                {liveDoubt.status === 'answered' ? '✓ Answered' : '● Open'}
              </span>
            </div>

            {liveDoubt.chapter && <p className={styles.chapterLabel}>Chapter: {liveDoubt.chapter}</p>}

            <div className={styles.questionBody}>
              {isAdmin && editingQuestion ? (
                <div className={styles.editWrap}>
                  <textarea
                    className={styles.editTextarea}
                    value={questionEditText}
                    onChange={e => setQuestionEditText(e.target.value)}
                    onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                    ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                    autoFocus
                  />
                  <div className={styles.editActions}>
                    <button className={styles.editCancelBtn} onClick={() => setEditingQuestion(false)}>Cancel</button>
                    <button className={styles.editSaveBtn} onClick={saveQuestion} disabled={savingEdit}>
                      <Check size={13} /> {savingEdit ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className={styles.editableField}>
                  <MathText text={liveDoubt.questionText} />
                  {isAdmin && (
                    <button
                      className={styles.editPencilBtn}
                      onClick={() => { setQuestionEditText(liveDoubt.questionText); setEditingQuestion(true); }}
                      title="Edit question"
                    >
                      <Pencil size={12} />
                    </button>
                  )}
                </div>
              )}
            </div>

            {liveDoubt.imageURL && (
              <div className={styles.imgWrap} style={{ marginTop: 14 }}>
                <div className={styles.imgInner}>
                  <img
                    src={liveDoubt.imageURL}
                    alt="Question"
                    className={styles.questionImage}
                    loading="lazy"
                    style={{ transform: `rotate(${qTransform.rotation}deg) scaleX(${qTransform.flipH ? -1 : 1}) scaleY(${qTransform.flipV ? -1 : 1})` }}
                  />
                </div>
                <ImgCtrl
                  t={qTransform}
                  onRotateL={() => setQTransform(t => ({ ...t, rotation: (t.rotation - 90 + 360) % 360 }))}
                  onRotateR={() => setQTransform(t => ({ ...t, rotation: (t.rotation + 90) % 360 }))}
                  onFlipH={() => setQTransform(t => ({ ...t, flipH: !t.flipH }))}
                  onFlipV={() => setQTransform(t => ({ ...t, flipV: !t.flipV }))}
                  onZoom={() => openLightbox(liveDoubt.imageURL, qTransform)}
                  onChangeImg={isAdmin && R2_ENABLED ? () => qImgInputRef.current?.click() : undefined}
                  onDeleteImg={isAdmin ? deleteQImg : undefined}
                />
              </div>
            )}
          </div>

          {/* Chat section */}
          <div className={styles.chatLabel}>
            💬 {replies.length === 0 ? 'No messages yet' : `${replies.length} message${replies.length !== 1 ? 's' : ''}`}
          </div>

          <div className={styles.chatThread}>
            {replies.length === 0 && (
              <div className={styles.noReplies}>
                <span>🎓</span>
                <p>{isAdmin ? 'Write your first answer below!' : 'Our team is reviewing your doubt. Check back soon for the answer!'}</p>
              </div>
            )}
            {replies.map(reply => {
              const isOwn = reply.userId === currentUser?.uid;
              const isAdminMsg = reply.roleLabel === 'PrrepX Team';
              // skip empty bubbles (text deleted, image already gone)
              if (!reply.text && !reply.imageURL) return null;
              // fall back to saved Firestore transform, then default
              const rt = replyTransforms[reply.id] || reply.imageTransform || { rotation: 0, flipH: false, flipV: false };
              const updateRt = (patch) => {
                const current = replyTransforms[reply.id] || reply.imageTransform || { rotation: 0, flipH: false, flipV: false };
                const next = { ...current, ...patch };
                setReplyTransforms(m => ({ ...m, [reply.id]: next }));
                updateDoc(doc(db, 'doubts', liveDoubt.id, 'replies', reply.id), { imageTransform: next }).catch(console.error);
              };
              return (
                <div key={reply.id} className={`${styles.msgRow} ${isOwn ? styles.msgRowOwn : ''}`}>
                  {!isOwn && (
                    reply.userPhotoURL
                      ? <img src={reply.userPhotoURL} alt="" className={styles.msgAvatar} referrerPolicy="no-referrer" loading="lazy" />
                      : <div className={`${styles.msgAvatar} ${styles.msgAvatarFallback}`}>{(reply.userName?.[0] ?? 'P').toUpperCase()}</div>
                  )}
                <div className={`${styles.bubble} ${isOwn ? styles.bubbleOwn : isAdminMsg ? styles.bubbleAdmin : styles.bubbleStudent}`}>
                    <div className={styles.bubbleHeader}>
                      <span className={`${styles.bubbleName} ${isOwn ? styles.bubbleNameOwn : ''}`}>
                        {/* students only see the team name; admins see the sender's real name */}
                        {isAdminMsg && !isAdmin ? 'PrrepX Pathshala Team' : (reply.userName || (isAdminMsg ? 'PrrepX Pathshala Team' : 'Student'))}
                      </span>
                      {isAdminMsg && !isOwn && (
                        <span className={styles.verifiedBadge} title="Verified · PrrepX Pathshala Team">
                          <BadgeCheck size={15} />
                        </span>
                      )}
                    </div>
                    {reply.text && (
                      isAdmin && isAdminMsg && editingReplyId === reply.id ? (
                        <div className={styles.editWrap}>
                          <textarea
                            className={styles.editTextarea}
                            value={replyEditText}
                            onChange={e => setReplyEditText(e.target.value)}
                            onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                            ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                            autoFocus
                          />
                          <div className={styles.editActions}>
                            <button className={styles.editCancelBtn} onClick={() => setEditingReplyId(null)}>Cancel</button>
                            <button className={styles.editSaveBtn} onClick={saveReply} disabled={savingEdit}>
                              <Check size={13} /> {savingEdit ? 'Saving…' : 'Save'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className={styles.editableField}>
                          <div className={styles.bubbleText}><MathText text={reply.text} /></div>
                          {isAdmin && isAdminMsg && (
                            <>
                              <button
                                className={`${styles.editPencilBtn} ${isOwn ? styles.editPencilBtnOwn : ''}`}
                                onClick={() => { setReplyEditText(reply.text); setEditingReplyId(reply.id); }}
                                title="Edit message"
                              >
                                <Pencil size={12} />
                              </button>
                              <button
                                className={`${styles.editPencilBtn} ${styles.editDeleteBtn} ${isOwn ? styles.editPencilBtnOwn : ''}`}
                                onClick={() => deleteReply(reply.id)}
                                title="Delete message"
                              >
                                <Trash2 size={12} />
                              </button>
                            </>
                          )}
                        </div>
                      )
                    )}
                    {reply.imageURL && (
                      <div className={styles.imgWrap}>
                        <div className={styles.imgInner}>
                          <img
                            src={reply.imageURL}
                            alt="Attachment"
                            className={styles.bubbleImg}
                            loading="lazy"
                            style={{ transform: `rotate(${rt.rotation}deg) scaleX(${rt.flipH ? -1 : 1}) scaleY(${rt.flipV ? -1 : 1})` }}
                          />
                        </div>
                        <ImgCtrl
                          t={rt}
                          onRotateL={() => updateRt({ rotation: (rt.rotation - 90 + 360) % 360 })}
                          onRotateR={() => updateRt({ rotation: (rt.rotation + 90) % 360 })}
                          onFlipH={() => updateRt({ flipH: !rt.flipH })}
                          onFlipV={() => updateRt({ flipV: !rt.flipV })}
                          onZoom={() => openLightbox(reply.imageURL, rt)}
                          onChangeImg={isAdmin && R2_ENABLED ? () => { imgEditTargetReplyId.current = reply.id; replyImgInputRef.current?.click(); } : undefined}
                          onDeleteImg={isAdmin ? () => deleteReplyImg(reply.id) : undefined}
                        />
                      </div>
                    )}
                    <span className={styles.bubbleTime}>
                      {formatDateTime(reply.createdAt)} · {timeAgo(reply.createdAt)}
                      {isAdmin && isAdminMsg && (
                        <span className={styles.seenArea}>
                          {(() => {
                            const replyDate = reply.createdAt?.toDate ? reply.createdAt.toDate() : new Date(reply.createdAt ?? 0);
                            const trackable = replyDate >= SEEN_TRACKING_START;
                            if (reply.seenAt) {
                              return <span className={styles.seenTick}><CheckCheck size={12} /></span>;
                            } else if (trackable) {
                              return <span className={styles.unreadTick}><CheckCheck size={12} /></span>;
                            }
                            return null; // legacy reply — seen status unknown
                          })()}
                          <button
                            className={styles.eyeBtn}
                            onClick={e => { e.stopPropagation(); setSeenPopup(p => p === reply.id ? null : reply.id); }}
                            title="Seen info"
                          >
                            <Eye size={12} />
                          </button>
                          {seenPopup === reply.id && (() => {
                            const replyDate = reply.createdAt?.toDate ? reply.createdAt.toDate() : new Date(reply.createdAt ?? 0);
                            const trackable = replyDate >= SEEN_TRACKING_START;
                            return (
                              <div className={styles.seenPopover}>
                                {reply.seenAt
                                  ? <><CheckCheck size={14} className={styles.seenPopoverIcon} /><span>Seen by student<br /><strong>{formatDateTime(reply.seenAt)}</strong></span></>
                                  : trackable
                                    ? <><CheckCheck size={14} className={styles.unreadPopoverIcon} /><span>Not yet seen by student</span></>
                                    : <><Eye size={14} className={styles.unreadPopoverIcon} /><span>Seen status unavailable<br /><strong style={{fontSize:10}}>Sent before tracking started</strong></span></>}
                              </div>
                            );
                          })()}
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>
        </div>

        {/* Hidden file inputs for admin image replacement */}
        {isAdmin && (
          <>
            <input ref={qImgInputRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} onChange={handleQImgFileChange} />
            <input ref={replyImgInputRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} onChange={handleReplyImgFileChange} />
          </>
        )}
        {imgEditSaving && (
          <div className={styles.imgEditSavingBanner}><Loader2 size={14} className={styles.spinner} /> Uploading image…</div>
        )}

        {/* Zoom lightbox — rendered via portal to escape overflow:hidden panel */}
        {lightbox && createPortal(
          <div className={styles.lbOverlay} onClick={closeLightbox}>
            <div className={styles.lbBox} onClick={e => e.stopPropagation()}>
              <div className={styles.lbBar}>
                <button className={styles.lbCtrlBtn} onClick={() => setLightbox(l => ({ ...l, rotation: (l.rotation - 90 + 360) % 360 }))} title="Rotate left"><RotateCcw size={15} /> <span>Rotate Left</span></button>
                <button className={styles.lbCtrlBtn} onClick={() => setLightbox(l => ({ ...l, rotation: (l.rotation + 90) % 360 }))} title="Rotate right"><RotateCw size={15} /> <span>Rotate Right</span></button>
                <button className={`${styles.lbCtrlBtn} ${lightbox.flipH ? styles.lbCtrlBtnOn : ''}`} onClick={() => setLightbox(l => ({ ...l, flipH: !l.flipH }))} title="Flip horizontal"><FlipHorizontal size={15} /> <span>Flip H</span></button>
                <button className={`${styles.lbCtrlBtn} ${lightbox.flipV ? styles.lbCtrlBtnOn : ''}`} onClick={() => setLightbox(l => ({ ...l, flipV: !l.flipV }))} title="Flip vertical"><FlipVertical size={15} /> <span>Flip V</span></button>
                <button className={styles.lbCloseBtn} onClick={closeLightbox} title="Close (Esc)"><X size={16} /></button>
              </div>
              <div className={styles.lbImgWrap}>
                <img
                  src={lightbox.src}
                  alt="Full size"
                  className={styles.lbImg}
                  style={{ transform: `rotate(${lightbox.rotation}deg) scaleX(${lightbox.flipH ? -1 : 1}) scaleY(${lightbox.flipV ? -1 : 1})` }}
                />
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Footer: admin reply box OR student info */}
        <div className={styles.footer}>
          {/* Status banner for student — shows live state changes */}
          {isStudent && liveDoubt.status === 'answered' && !liveDoubt.studentHasReplied && (
            <div className={styles.repliedBanner}>
              ✅ PrrepX Team has replied to your doubt — scroll up to read, then follow up below if needed.
            </div>
          )}
          {isStudent && liveDoubt.status !== 'answered' && liveDoubt.lastRepliedByRole === 'student' && (
            <div className={styles.followupBanner}>
              ↗️ Follow-up sent — PrrepX Team will reply soon.
            </div>
          )}
          {canReply ? (
            <div className={styles.replyInput}>
              {/* Image preview strip */}
              {replyImagePreview && (
                <div className={styles.imgPreviewWrap}>
                  <img src={replyImagePreview} alt="Preview" className={styles.imgPreview} />
                  <button className={styles.imgRemoveBtn} onClick={clearImage} aria-label="Remove image">
                    <X size={14} />
                  </button>
                </div>
              )}
              <textarea
                className={styles.textarea}
                placeholder={isAdmin ? "Write your answer or solution… (Ctrl+Enter to send)" : "Ask a follow-up question…"}
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSend(); }}
                rows={3}
                maxLength={2000}
              />
              <div className={styles.replyActions}>
                {R2_ENABLED && (
                  <>
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      style={{ display: 'none' }}
                      onChange={handleImageSelect}
                    />
                    <button
                      className={`${styles.imgBtn} ${replyImageFile ? styles.imgBtnActive : ''}`}
                      onClick={() => imageInputRef.current?.click()}
                      title="Attach solved-question image"
                      disabled={sending}
                    >
                      <Image size={16} />
                      {replyImageFile ? 'Change Photo' : 'Attach Photo'}
                    </button>
                  </>
                )}
                <span className={styles.charCount}>{replyText.length}/2000 · Ctrl+Enter</span>
                <button
                  className={styles.sendBtn}
                  onClick={handleSend}
                  disabled={(!replyText.trim() && !replyImageFile) || sending}
                >
                  {sending ? (
                    <><Loader2 size={15} className={styles.spinner} /> {uploadingImg ? 'Uploading…' : 'Sending…'}</>
                  ) : (
                    <><Send size={15} /> {isAdmin ? 'Send Answer' : 'Send Follow-up'}</>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <p className={styles.studentNote}>
              💬 PrrepX Team will answer your doubt soon. Answers appear here in real-time!
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
